import { useSelector } from 'react-redux';
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { isSendInvitesAllowed } from '../utils/access';
import RenderUpdatePo from '../pages/Payments/Modals/RenderUpdatePo';
import { redirectUrlAuth } from './Routes';
import { dashboardTab } from '../layout/sidebar';
import { getOrgId } from '../utils/common';

const Payment = lazy(() => import('../pages/Payments/Payments'));
const Projects = lazy(() => import('../pages/project/Projects'));
const Ticket = lazy(() => import('../pages/Ticket'));
const Inventory = lazy(() => import('../pages/Inventory'));
const Chat = lazy(() => import('../pages/chat'));
const CustomerBilling = lazy(() => import('../pages/CustomerBilling'));
const Profile = lazy(() => import('../pages/profile/Profile'));
const ResourceContainer = lazy(() => import('../pages/Resources/ResourceContainer'));
const Wallet = lazy(() => import('../pages/Wallet'));
const AddPOForm = lazy(() => import('../pages/Payments/components/AddPOForm'));
const TodayStatus = lazy(() => import('../pages/Ticket/TodayStatus'));
const Onboarding = lazy(() => import('../pages/Onboard'));
const TicketExecution = lazy(() => import('../pages/ticket-execution'));
const EditTicket = lazy(() => import('../pages/edit-ticket'));
const ClientsTab = lazy(() => import('../pages/Clients/ClientsTab'));
const UserProfile = lazy(() => import('../pages/UserProfile'));
const RaisePo = lazy(() => import('../pages/raise-po'));
const EditPayoutpage = lazy(() => import('../pages/edit-payout'));
const Details = lazy(() => import('../pages/Details'));
const TrackPage = lazy(() => import('../pages/SupportTrack/Track'));
const SendInvitesContainer = lazy(() => import('../pages/team/SendInviteContainer'));
const GrafanaDashboard = lazy(() => import('../components/GrafanaDashboard'));
const UsersPage = lazy(() => import('../pages/users'));
const QuickTicket = lazy(() => import('../pages/QuickTicket'));
const ConnectLogin = lazy(() => import('../pages/ConnectLogin'));

export const ModulesPathKey = {
    dashboard: 'dashboard',
    project: 'project',
    team: 'team',
    ticket: 'ticket',
    chat: 'chat',
    inventory: 'inventory',
    payment: 'payment',
    wallet: 'wallet',
    customer_billing: 'customer_billing',
    chat_page: 'chat_page',
    user_profile: 'user_profile',
    send_invite: 'send_invite',
    edit_payout: 'edit_payout',
    raise_po: 'raise_po',
    update_po: 'update_po',
    connect: 'connect',
}

const withoutLayout = ({ children }) => {
    return children;
};

const loader = (<span>Loading...</span>);
const home = {
    path: '/home',
    component: <div>Home</div>,
};
export const projects = {
    key: ModulesPathKey.project,
    path: '/projects',
    component: (
        <Suspense fallback={loader}>
            <Projects />
        </Suspense>
    ),
};
const ticketsExecution = {
    path: '/tickets/:ticketId/execute/:step',
    component: (
        <Suspense fallback={loader}>
            <TicketExecution />
        </Suspense>
    ),
};
export const teamWork = {
    key: ModulesPathKey.team,
    path: '/teams',
    component: (
        <Suspense fallback={loader}>
            <UsersPage />
        </Suspense>
    ),
};

const ticketEdit = {
    key: ModulesPathKey.ticket,
    path: '/tickets/:ticketId/edit',
    component: (
        <Suspense fallback={loader}>
            <EditTicket />
        </Suspense>
    ),
};

export const inventory = {
    key: ModulesPathKey.inventory,
    path: '/inventory',
    component: (
        <Suspense fallback={loader}>
            <Inventory />
        </Suspense>
    ),
};

const profile = {
    path: '/profile/:profileId',
    component: (
        <Suspense fallback={loader}>
            <Profile />
        </Suspense>
    ),
    layout: withoutLayout,
};

const resources = {
    path: '/documents',
    component: (
        <Suspense fallback={loader}>
            <ResourceContainer />
        </Suspense>
    ),
};

export const payments = {
    key: ModulesPathKey.payment,
    path: '/payments',
    component: (
        <Suspense fallback={loader}>
            <Payment />
        </Suspense>
    ),
};

export const wallet = {
    key: ModulesPathKey.wallet,
    path: '/wallet',
    component: (
        <Suspense fallback={loader}>
            <Wallet />
        </Suspense>
    ),
};

const addNewPO = {
    path: '/addNewPO',
    component: (
        <Suspense fallback={loader}>
            <AddPOForm />
        </Suspense>
    ),
};

const userProfile = {
    key: ModulesPathKey.user_profile,
    path: '/user-profile',
    component: (
        <Suspense fallback={loader}>
            <UserProfile />
        </Suspense>
    ),
};

export const ticket = {
    key: ModulesPathKey.ticket,
    path: '/tickets',
    component: (
        <Suspense fallback={loader}>
            <Ticket />
        </Suspense>
    ),
};

export const todayStatus = {
    path: '/today-status',
    component: (
        <Suspense fallback={loader}>
            <TodayStatus />
        </Suspense>
    ),
};

export const chats = {
    key: ModulesPathKey.chat,
    path: '/chats',
    component: (
        <Suspense fallback={loader}>
            <Chat />
        </Suspense>
    ),
};


export const chatPageUsingLink = {
    key: ModulesPathKey.chat_page,
    path: '/chats/:chatId',
    component: (
        <Suspense fallback={loader}>
            <Chat />
        </Suspense>
    ),
};

export const customerBilling = {
    key: ModulesPathKey.customer_billing,
    path: '/customer-billing',
    component: (
        <Suspense fallback={loader}>
            <CustomerBilling />
        </Suspense>
    ),
};

export const spOnboard = {
    path: '/onboarding/sp',
    component: (
        <Suspense fallback={loader}>
            <Onboarding />
        </Suspense>
    ),
};

export const raisePo = {
    key: ModulesPathKey.raise_po,
    path: `${ticket.path}/raise-po`,
    component: (
        <Suspense fallback={loader}>
            <RaisePo />
        </Suspense>
    ),
};

export const updatePo = {
    key: ModulesPathKey.update_po,
    path: `${payments.path}/po/:poId`,
    component: (
        <Suspense fallback={loader}>
            <RenderUpdatePo />
        </Suspense>
    ),
};

const clientsTab = {
    path: '/clients',
    component: (
        <Suspense fallback={loader}>
            <ClientsTab />
        </Suspense>
    ),
};

const editPayout = {
    key: ModulesPathKey.edit_payout,
    path: `${ticket.path}/:ticketId/edit-payout`,
    component: (
        <Suspense fallback={loader}>
            <EditPayoutpage />
        </Suspense>
    ),
};

export const trackSupportTicket = {
    path: `/support/track`,
    component: (
        <Suspense fallback={loader}>
            <QuickTicket />
        </Suspense>
    ),
};

export const detailsPath = {
    path: `/details`,
    component: (
        <Suspense fallback={loader}>
            <Details />
        </Suspense>
    ),
};

export const ticketTrackPath = {
    path: `/track`,
    component: (
        <Suspense fallback={loader}>
            <TrackPage />
        </Suspense>
    ),
};

export const sendInvitePath = {
    key: ModulesPathKey.send_invite,
    path: `/send-invite`,
    component: (
        <Suspense fallback={loader}>
            <SendInvitesContainer />
        </Suspense>
    ),
};

export const connectLogin = {
    key: ModulesPathKey.connect,
    path: `/connect`,
    component: (
        <Suspense fallback={loader}>
            <ConnectLogin />
        </Suspense>
    ),
};

const Dashboard = () => {
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const [dashboardUrl, setDashboardUrl] = useState('');

    useEffect(() => {
        if (newLoggedUserData.org.grafana_dashboard_url) {
            setDashboardUrl(newLoggedUserData.org.grafana_dashboard_url);
        }
    }, [newLoggedUserData.org.grafana_dashboard_url]);


    return <GrafanaDashboard dashboardUrl={dashboardUrl} setDashboardUrl={setDashboardUrl} />
    // if (loggedUser.grafana_url) window.open(loggedUser.grafana_url, '_blank')
    // return <></>
}

export const dashBoard = {
    key: ModulesPathKey.dashboard,
    path: `/dashboard`,
    component: (
        <Suspense fallback={loader}>
            <Dashboard />
        </Suspense>
    ),
};

export const onboardPath = {
    path: '/onboarding',
    component: (
        <Suspense fallback={loader}>
            <Onboarding />
        </Suspense>
    ),
};

export const getProtectedOnboardingRoutes = ({ is_onboarded }) => {
    if (is_onboarded) return [];
    return onboardPath;
};


export const getProtectedSidebarRoutes = ({ is_onboarded, sidebarList, actions = [] }) => {
    if (!is_onboarded) return [];
    let routes = [projects, ticket, teamWork, inventory, sendInvitePath, payments, chats, chatPageUsingLink, editPayout, wallet, customerBilling, userProfile, ticketEdit, raisePo, updatePo, dashBoard, connectLogin];
    return routes.filter((route) => {
        const sendInviteAllowed = isSendInvitesAllowed(actions);
        if (route.key === ModulesPathKey.user_profile) return true;
        if (route.key === ModulesPathKey.send_invite) return sendInviteAllowed;
        if (route.key === ModulesPathKey.edit_payout) return true;
        if (route.key === ModulesPathKey.raise_po) return true;
        if (route.key === ModulesPathKey.update_po) return true;
        if (route.key === ModulesPathKey.dashboard) return true;
        if (route.key === ModulesPathKey.connect) return true;
        const find = sidebarList && sidebarList.find(({ key }) => key === route.key);
        if (route.key === ModulesPathKey.chat_page && sidebarList?.find(({ key }) => key === ModulesPathKey.chat)) return true;
        return find ? true : false;
    });
};

export const getDefaultRoute = ({ is_onboarded, sidebarList, actions = [] }) => {
    // Check for stored redirect URL first, before determining default path
    const findLocal = localStorage.getItem(redirectUrlAuth);

    if (findLocal) {
        try {
            const url = new URL(findLocal);
            const redirectPath = url.pathname + url.search;

            localStorage.removeItem(redirectUrlAuth);
            if (is_onboarded) {
                const validRoutes = getProtectedSidebarRoutes({ is_onboarded, sidebarList, actions });
                const validPaths = validRoutes.map(route => route.path);

                const isValidPath = validPaths.some(validPath => {
                    if (validPath === redirectPath) return true;

                    const pathPattern = validPath.replace(/:[^/]+/g, '[^/]+');
                    const regex = new RegExp(`^${pathPattern}$`);
                    return regex.test(url.pathname);
                });

                if (isValidPath) {
                    return redirectPath;
                }
            }

        } catch (error) {
            console.error('Error parsing redirect URL:', error);
            localStorage.removeItem(redirectUrlAuth);
        }
    }

    const protectedSidebarList = getProtectedSidebarRoutes({ is_onboarded, sidebarList, actions });
    let defaultPath = protectedSidebarList[0]?.path;

    if (is_onboarded && defaultPath) {
        const orgId = getOrgId();
        if (orgId === 'org_2wjcB9HGJSLRNuLeIyyciKE3VHO') {
            defaultPath = payments.path;
            return defaultPath;
        };

        return defaultPath;
    } else {
        return `${onboardPath.path}`;
    }
};